import { SentryError } from '@ifixit/sentry';
import ValidationFunctions from 'Shared/validation';
import { ignoreApplicationErrors } from 'Shared/utils/application-error';
import { onChildWindowClose } from 'Shared/utils/on-child-window-close';
import { timeoutPromise } from 'Shared/utils/timeout-promise';
import { UnsavedChanges, hasUnsavedChanges } from 'Shared/utils/unsaved-changes';
import { throttler, rateLimiter } from 'Shared/utils/throttle';
import { fireGlobal, listenGlobal } from 'Shared/utils/global-events';
import { clickSafe } from 'Shared/utils/click-safe';
import { onHashFragmentElement } from 'Shared/utils/hash-fragment';
import { createElements, createAlert, createAlertPrompt } from 'Shared/utils/create-elements';

// GLOBAL CONVENIENCE FUNCTIONS ///////////////////////////////////////////////

/**
 * Convenience function to call Element:getElement with the document as the
 * context object.
 */
export { $E };
window.$E = $E;
function $E(selector) {
   return document.getElement(selector);
}

export { parseDomain } from 'Shared/utils/parse-domain';
import { parseDomain } from 'Shared/utils/parse-domain';

window.parseDomain = parseDomain;

export { getURLParameter } from 'Shared/utils/url-params';
import { getURLParameter } from 'Shared/utils/url-params';

window.getURLParameter = getURLParameter;

/**
 * As close as we can get to a new keyword, `when` makes sure that its first
 * argument is non-empty (or non-false), and then executes its second argument,
 * passing in the first.
 */
export { when };
window.when = when;
function when(obj, fn, fallback) {
   if (!obj || obj.length === 0) {
      return fallback;
   }

   return fn(obj);
}

export { clickSafe } from 'Shared/utils/click-safe';
window.clickSafe = clickSafe;

export { onLoad, onDomReady } from 'Shared/utils/dom-ready';
import { onLoad, onDomReady } from 'Shared/utils/dom-ready';

window.onLoad = onLoad;
window.onDomReady = onDomReady;

export { onHashFragmentElement } from 'Shared/utils/hash-fragment';
window.onHashFragmentElement = onHashFragmentElement;

export { fireGlobal, listenGlobal } from 'Shared/utils/global-events';
window.fireGlobal = fireGlobal;
window.listenGlobal = listenGlobal;

/**
 * A debug helper that wraps every method of an element with a log message
 * that indicates that that method was called. Useful for seeing what sequence
 * of calls are being made on an element from various places. The optional
 * logger argument takes the element, method name, and method arguments as
 * parameters and can do whatever it wants with them.
 */
export { watch };
window.watch = watch;
function watch(el, logger) {
   if (!logger) {
      logger = function (el, prop, args) {
         // eslint-disable-next-line no-console
         console.log(prop); // @suppress console me
      };
   }
   for (let prop in el) {
      if (typeof el[prop] == 'function') {
         (function (prop, func) {
            el[prop] = function () {
               logger(el, prop, arguments);
               return func.apply(el, arguments);
            };
         })(prop, el[prop]);
      }
   }
}

export { ApplicationError } from 'Shared/utils/application-error';
import { ApplicationError } from 'Shared/utils/application-error';

window.ApplicationError = ApplicationError;

export { mobileDetected, tabletDetected } from 'Shared/utils/mobile-detect';
import { mobileDetected } from 'Shared/utils/mobile-detect';
import { tabletDetected } from 'Shared/utils/mobile-detect';

window.mobileDetected = mobileDetected;
window.tabletDetected = tabletDetected;

// FUNCTION EXTENSIONS ////////////////////////////////////////////////////////

Function.implement({
   /**
    * Extend functions with 'wrap', which lets us wrap a function g around a
    * function f, giving g full control over whether or not f gets called, and
    * with what arguments:
    *
    *    function f(msg) {alert(msg)};
    *    var g = f.wrap(function(h, msg) {h('From g: ' + msg)});
    *    g('Hello, World!') => "From g: Hello, World!"
    */
   wrap: function (wrapper) {
      let __method = this;
      return function () {
         return wrapper.apply(this, [__method.bind(this)].concat(Array.convert(arguments)));
      };
   },
});

// ELEMENT EXTENSIONS /////////////////////////////////////////////////////////

/**
 * Extend the Element class with our own custom methods.
 */
Element.implement({
   /**
    * Centers the element on the page, based on the viewport and element
    * dimensions as gleaned from Window.getSize() and Element.getSize(),
    * respectively.
    */
   center: function (options) {
      let el = this.getDimensions();
      let win = window.getSize();
      let scroll = options && options.position == 'top' ? { x: 0, y: 0 } : window.getScroll();

      let left = Math.round(scroll.x + (win.x - el.x) / 2);
      let top = Math.round(scroll.y + (win.y - el.y) / 2);

      this.setStyles({
         left: (left < 0 ? 5 : left) + 'px',
         top: (top < 0 ? 5 : top) + 'px',
      });

      return this;
   },

   /**
    * Adds the 'disabled' attribute. If the element is a button (has the
    * buttonLink class) then add our disabled button style.
    */
   disable: function () {
      this.set('disabled', true);

      if (this.hasClass('buttonLink')) {
         this.addClass('buttonLinkDisabled');
      }
      return this;
   },

   /**
    * Removes the 'disabled' attribute if it exists.
    * If the element is a button and has our buttonLinkDisabled style, remove
    * it.
    */
   enable: function () {
      this.removeAttribute('disabled');

      if (this.hasClass('buttonLinkDisabled')) {
         this.removeClass('buttonLinkDisabled');
      }
      return this;
   },

   /**
    * Sets the element's visibility property to the default value, making the
    * element visible. Returns the element for chaining.
    */
   makeVisible: function () {
      this.setStyle('visibility', '');
      return this;
   },

   /**
    * Sets the element's visibility property to 'hidden', making the element
    * invisible, but keeping it in the page flow. Returns the element for
    * chaining.
    */
   makeInvisible: function () {
      this.setStyle('visibility', 'hidden');
      return this;
   },

   /**
    * Returns true if the element is visible on the page, false otherwise.
    * Taken from http://stackoverflow.com/questions/1056357 (best way to find
    * if a dom object is visible or not using mootools) and modified very
    * slightly to negate the logic.
    */
   visible: function () {
      let w = this.offsetWidth,
         h = this.offsetHeight;
      let force = this.tagName === 'TR';
      let hidden =
         w === 0 && h === 0 && !force
            ? true
            : w !== 0 && h !== 0 && !force
              ? false
              : this.getStyle('display') === 'none';
      return !hidden;
   },

   /**
    * Alias for Element.set('html').
    */
   update: function (str) {
      return this.set('html', str);
   },

   /**
    * Sets or toggles the visibility of this element.
    * Calls show() if visibility is true, hide() if it's false
    * and toggles the element if the argument is left out
    */
   toggle: function (visibility) {
      let show = arguments.length > 0 ? visibility : !this.visible();
      return show ? this.show() : this.hide();
   },

   /**
    * Gives focus to and tries to select a form field.
    */
   activate: function () {
      let tag = this.get('tag');
      let type = this.get('type');

      try {
         this.focus();
         if (
            this.select != undefined &&
            (tag == 'textarea' || (tag == 'input' && ['text', 'password'].contains(type)))
         ) {
            this.select();
         }
         // eslint-disable-next-line no-empty
      } catch {}

      return this;
   },

   /**
    * Returns true if the element is 100% within the bounding rectangle of the
    * browser window i.e. the document doesn't need to be scrolled for it to be
    * visible.
    *
    * Note: doesn't take into account css visibility nor nested scrolling with
    * overflow: hidden.
    *
    * Taken from here: http://stackoverflow.com/a/7557433
    */
   isOnScreen: function () {
      let rect = this.getBoundingClientRect();

      return (
         rect.top >= 0 &&
         rect.left >= 0 &&
         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
         rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
   },

   /**
    * Tries to scroll to the element smoothly.
    */
   jumpTo: function (options) {
      options = options || {};
      let offset = options.offset === undefined ? 100 : options.offset;
      let behavior = options.behavior || 'smooth';

      window.scrollTo({
         top: this.getPosition().y - offset,
         behavior: behavior,
      });
   },

   /**
    * Returns the zIndex of the first element containing this element (or of
    * this element itself) which has a zIndex that isn't 'auto'; if no element
    * in the stack has a zIndex other than 'auto', then returns 'auto'.
    */
   getEffectiveZindex: function () {
      let itr = this,
         zIndex;
      // eslint-disable-next-line no-empty
      while ((zIndex = itr.getStyle('zIndex')) == 'auto' && (itr = itr.getParent())) {}
      return zIndex;
   },

   isFixed: function () {
      let el = this;
      do {
         if (
            el.getStyle('position') == 'fixed' ||
            el.retrieve('pinnedByJS') ||
            el.retrieve('fixedPosition')
         ) {
            return true;
         }
      } while ((el = $(el.offsetParent)));
      return false;
   },

   /**
    * Forces the element to retain it's current dimensions
    * even if the contents change.
    *
    * See: unpinDimensions()
    */
   pinDimensions: function () {
      let size = this.getComputedSize();
      let styles = {
         height: size.height,
         width: size.width,
         display: 'block',
      };
      this.setStyles(styles);
   },

   /**
    * Undoes the action of pinDimensions(), allowing an element to be sized
    * by CSS and it's contents.
    */
   unpinDimensions: function () {
      let styles = {
         height: '',
         width: '',
         display: '',
      };
      this.setStyles(styles);
   },
});

String.implement({
   // Turns text into safe html.
   encodeHtml: function () {
      return String(this)
         .replace(/&/g, '&amp;')
         .replace(/"/g, '&quot;')
         .replace(/'/g, '&#39;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;');
   },

   rtrim: function (toTrim) {
      return this.replace(new RegExp(toTrim + '$', ''), '');
   },
});

// WINDOW EXTENSIONS //////////////////////////////////////////////////////////

window.isFixed = function () {
   return false;
};

// CUSTOM EVENTS //////////////////////////////////////////////////////////////

/**
 * A custom event that fires on any mousedown outside of the element the event
 * is attached to. Thanks to:
 *
 * http://appden.com/javascript/fun-with-custom-events-on-elements-in-mootools/
 */
Element.Events.clickoff = {
   base: 'click',
   condition: function (event) {
      // Stop event from bubbling up; note that it's important to use
      // stopPropagation() instead of stop() here, or clicking links inside the
      // element won't work.
      event.stopPropagation();
      return false;
   },
   onAdd: function (fn) {
      this.getDocument().addEvent('click', fn);
   },
   onRemove: function (fn) {
      this.getDocument().removeEvent('click', fn);
   },
};

// REQUEST EXTENSIONS /////////////////////////////////////////////////////////

if (window.Request == undefined) {
   window.Request = {};
}

Request.activeRequests = new Map();

Request.Batch = {
   options: {
      period: 1500,
   },

   hash: new Hash(),
   pinging: false,
   index: 0,

   add: function (req) {
      req.batchId = this.index++;
      this.hash.set(req.batchId, req);
      this.start();
   },

   remove: function (req) {
      if (!this.hash.has(req.batchId)) {
         return false;
      }

      this.hash.erase(req.batchId);
      if (!this.hash.getLength()) {
         this.stop();
      }

      return true;
   },

   ping: function () {
      this.pinging = true;

      let args = [];
      this.hash.each((req, key) => {
         args.push([key, req.funcName, req.args]);
      });

      new Request.AjaxIO('Request.Batch', {
         onSuccess: function (result) {
            result.each(batch => {
               let id = batch[0];
               let response = JSON.parse(batch[1]);

               if (response.AjaxIO.complete) {
                  this.hash.get(id).fireEvent('complete', [response.AjaxIOData]);
                  this.hash.erase(id);
               }
            });

            // Stop repeating if every request has been filled
            if (this.hash.getLength()) {
               this.nextPing = this.ping.delay(this._period, this);
               // exponential backoff.
               this._period *= 1.1;
            } else {
               this.pinging = false;
            }
         }.bind(this),
      }).send(args);
   },

   start: function () {
      // Always reset the period to the base value
      this._period = this.options.period;
      if (!this.pinging) {
         this.ping();
      }
   },

   stop: function () {
      this.pinging = false;
      clearInterval(this.nextPing);
   },
};

/**
 * new Request.AjaxIO(funcName, {
 *    // options...
 * }).send(arg1, arg2, ...);
 */
export default (Request.AjaxIO = new Class({
   Implements: [Options, Events],

   options: {
      onSuccess: function () {},
      timeout: 0,
   },

   initialize: function (funcName, options) {
      this.setOptions(options);
      this.funcName = funcName;

      let self = this;
      let url = this.options.url ? this.options.url : window.location.toString().split('#')[0];

      // Safari is the only browser that returns window.location as a decoded string.
      // This is only a problem for % literals.

      // We don't check the Browser.Engine here because Chrome shares webkit, but doesn't
      // have this same problem. This regex should be safe to use everywhere.
      url = url.replace(/%(?![\dA-Fa-f]{2})/, '%25');

      this.request = new Request({
         url: url,
         onSuccess: this.onSuccess.bind(this),
         onComplete: function () {
            Request.activeRequests.delete(self);
            Request.completedRequests++;
            self.fireEvent('complete');
         },
         onCancel: function () {
            Request.activeRequests.delete(self);
            self.fireEvent('cancel');
            self._reject();
         },
         onFailure: function () {
            self.fireEvent('error');
            self._reject();
         },
      });
   },

   send: function () {
      let _this = this;
      let args = Array.convert(arguments);
      return new Promise((resolve, reject) => {
         _this._resolve = resolve;
         _this._reject = reject;

         let post = {
            AjaxIO_function: _this.funcName,
            AjaxIO_args: JSON.stringify(args),
         };

         Request.activeRequests.set(_this, post);

         _this.request.send(Hash.toQueryString(post));
      });
   },

   ping: function () {
      this.args = Array.convert(arguments);
      Request.Batch.add(this);
      if (this.options.timeout > 0) {
         this.timeout.delay(this.options.timeout, this);
      }
   },

   timeout: function () {
      if (Request.Batch.remove(this)) {
         this.fireEvent('timeout');
      }
   },

   onSuccess: function (text) {
      let response = JSON.parse(text);
      if (response.error) {
         this.fireEvent('onError', [response]);
         this._reject(response.error);
         if (window.console && console.error) {
            console.error(response.error);
         }
      } else {
         if (response.AjaxIOData === undefined) {
            this._reject();
            if (window.console && console.error) {
               console.error('No data in response');
            }
         } else {
            this.fireEvent('onSuccess', [response.AjaxIOData]);
            this._resolve(response.AjaxIOData);
         }
      }
   },
}));
/**
 * A count of currently in-flight requests.
 *
 * Added so we can wait for ajax requests to finish in selenium tests by
 * waiting for this to be 0.
 */

Request.completedRequests = 0;

// For now lets enable this only for development for a while to make sure we
// don't run into any weird unintended behavior
if (!App.isProduction) {
   window.addEventListener('beforeunload', e => {
      let activeAjaxRequest = function () {
         return (
            window.Request !== undefined &&
            window.Request.activeRequests &&
            window.Request.activeRequests.size > 0
         );
      };

      if (activeAjaxRequest()) {
         // Prompt the user asking them if they are sure they want to
         // leave due to unsaved changes.
         e.returnValue = '';

         // Fail during development to prevent flakiness/unexpected
         // behavior if requests are in flight while trying to leave the page.
         let activeAjaxRequests = JSON.stringify([...window.Request.activeRequests.values()]);

         let errorMessage = `
            Active AJAX request(s) in flight:
            ${activeAjaxRequests}
         `;

         throw new SentryError(errorMessage);
      }
   });
}

// ASSET EXTENSIONS ///////////////////////////////////////////////////////////

export { SettingsCookie } from 'Shared/utils/settings-cookie';
import { SettingsCookie } from 'Shared/utils/settings-cookie';

window.SettingsCookie = SettingsCookie;

/**
 * Works like a Group, but for a collection of Assets, which must have their
 * onload event handlers installed on instantiation and not after the fact.
 * Takes any number of arrays of hrefs (or plain href strings), creates Assets
 * for each href (according to the extension), and provides events for the
 * successful loading of all js, all css, and all files together.
 *
 * Events:
 *
 *    onCssComplete -- fired when all css files have loaded
 *    onJsComplete  -- fired when all js files have loaded
 *    onComplete    -- fired when all css and js files have loaded
 *
 * Warning:
 *
 *    You *must* provide an options object as the last argument to the
 *    constructor, which should have one or more of the events as attributes.
 *    Adding events to the group after the fact is risky, and could result in
 *    missing a load event.
 */
// eslint-disable-next-line no-var
export var AssetGroup = (window.AssetGroup = new Class({
   Implements: [Options, Events],

   options: {
      onCssComplete: function () {},
      onJsComplete: function () {},
      onComplete: function () {},
   },

   initialize: function () {
      this.cssCount = 0;
      this.cssLoaded = false;
      this.jsCount = 0;
      this.jsLoaded = false;

      let args = Array.convert(arguments);
      let options = args.pop();
      this.setOptions(options);

      let scripts = $$('script');
      let links = $$('link[rel="stylesheet"]');

      args.each(function (arg) {
         Array.convert(arg).each(function (file) {
            if (/\.js\b/.test(file)) {
               if (!scripts.some(s => s.src == file)) {
                  this.jsCount += 1;
                  new Asset.javascript(file, {
                     onload: this.loaded.bind(this, 'js'),
                  });
               }
            } else if (/\.css\b/.test(file) && !links.some(l => l.href == file)) {
               // https://mootools.lighthouseapp.com/projects/2706/tickets/186-asset-css-onload-event-does-not-fire-in-ff3-safari-opera
               // Asset.css.onload does not fire for third-party domains, so disable it
               this.cssCount += 1;
               new Asset.css(file, {
                  onload: this.loaded.bind(this, 'css'),
               });
               //this.loaded.delay(100, this, 'css');
            }
         }, this);
      }, this);

      // just in case we ended up not needing to add any new js or css files,
      // we check our lists of assets to see if firing events is in order
      this.fireEvents();
   },

   loaded: function (which) {
      this[which + 'Count'] -= 1;
      this.fireEvents();
   },

   // fires events in a very stateful manner, checking the queues of js and
   // css files to see if they're empty and if we haven't already fired off
   // events
   fireEvents: function () {
      if (this.cssCount <= 0 && !this.cssLoaded) {
         this.fireEvent('onCssComplete');
         this.cssLoaded = true;
      }
      if (this.jsCount <= 0 && !this.jsLoaded) {
         this.fireEvent('onJsComplete');
         this.jsLoaded = true;
      }
      if (this.cssCount <= 0 && this.jsCount <= 0) {
         this.fireEvent('onComplete');
         this.cssCount = this.jsCount = 0;
      }
   },
}));

// FORM CLASS /////////////////////////////////////////////////////////////////

export { FormLibrary } from 'Shared/utils/form-library';
import { FormLibrary } from 'Shared/utils/form-library';

window.FormLibrary = FormLibrary;

window.Validation = ValidationFunctions;

// UTILS CLASS ////////////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var Utils = (window.Utils = (function () {
   return {
      log: function (body) {
         new Request.API_2_0('js_log/debug', {
            method: 'post',
            onSuccess: function (response) {
               return true;
            },
         }).send(body);

         return true;
      },

      ignoreApplicationErrors,

      onChildWindowClose,

      UnsavedChanges,

      hasUnsavedChanges,

      createElements,

      throttler,

      rateLimiter,

      createAlert,

      createAlertPrompt,

      passEvent: _passEvent,
      EventsFunctions: _EventsFunctions,

      timeoutPromise,
   };
})());

import {
   EventsFunctions as _EventsFunctions,
   passEvent as _passEvent,
} from 'Shared/utils/event-emitter';

export { EventsFunctions, passEvent } from 'Shared/utils/event-emitter';

export { htmlEncode } from 'Shared/utils/html-encode';
import { htmlEncode } from 'Shared/utils/html-encode';

window.htmlEncode = htmlEncode;

export { Geo } from 'Shared/utils/geo';

// Our tiny promise library
/**
 * ========================
 * NOTE: DEPRECATED! Please use the standards compliant Promise object.
 * ========================
 *
 * A dead simple promise / futures implementation
 *
 * Caller
 * ======
 *
 * var data = getStuffFromServer();
 * data.getValue(function ready(response) {
 *    // do something with response
 * });
 *
 * function getStuffFromServer() {
 *    var data = new Future();
 *    new Request({
 *       ...
 *       onComplete: function(response) {
 *          data.resolve(response);
 *       }
 *    });
 *    return data;
 * }
 *
 */
export { Future };
window.Future = Future;
function Future() {
   this.callbacks = [];
   this.resolved = false;
}

(function (prototype) {
   /**
    * Resolve this promise with the specified value
    *
    * Effectively "set" the value that will be passed to the getValue callbacks
    */
   prototype.resolve = function (resolution) {
      let self = this;
      if (self.resolved) {
         throw "Can't resolve a promise more than once";
      }

      self.resolution = resolution;
      self.resolved = true;

      self.callbacks.each(callback => {
         callback(resolution, self._error);
      });
      self.callbacks = [];
   };

   /**
    * Resolve this promise with an error.
    *
    * `error` will be passed to each callback like so:
    * callback(value, error)
    */
   prototype.error = function (error) {
      this._error = error;
      this.resolve();
   };

   /**
    * Get the value of this promise when it's ready. The resolved value will
    * be passed to the readyCallback().
    *
    * readyCallback: function(value, error) {}
    *
    * Returns: true if this promise is already resolved and readyCallback
    * was called immediately.
    *
    * Note: error is normally null and is of course, not required
    */
   prototype.getValue = function (readyCallback) {
      if (this.resolved) {
         readyCallback(this.resolution, this._error);
         return true;
      } else {
         this.callbacks.push(readyCallback);
         return false;
      }
   };

   /**
    * Returns true if the promise has been resolved.
    */
   prototype.isResolved = function () {
      return this.resolved;
   };
})(Future.prototype);

/**
 * Add the 'beforePrint' and 'afterPrint' events to window. Note: these may
 * fire multiple times (including before/after) while actually printing.
 *
 * Use them like so:
 * window.addEvent('beforePrint', function() {
 *    ...
 * });
 *
 * Derived from the code seen here:
 * http://tjvantoll.com/2012/06/15/detecting-print-requests-with-javascript/
 */
(function () {
   let inPrintMode = false;
   function beforePrint() {
      // Prevent multiple firings
      if (inPrintMode) {
         return;
      }
      window.fireEvent('beforePrint');
   }
   function afterPrint() {
      inPrintMode = false;
      window.fireEvent('afterPrint');
   }

   if (window.matchMedia) {
      let mediaQueryList = window.matchMedia('print');
      if (mediaQueryList.matches) {
         beforePrint();
      }
      mediaQueryList.addListener(mql => {
         if (mql.matches) {
            beforePrint();
         } else {
            afterPrint();
         }
      });
   }

   window.onbeforeprint = beforePrint;
   window.onafterprint = afterPrint;
})();

export { goToLastPage, getLastPage } from 'Shared/utils/last-page';
